import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { Arg, Entity, Field, Mutation, Subscription } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { getCollection, getCollectionHandle } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import { subscribePaginatedAggregation } from '@adornis/baseql/server/resolver/mongo.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { DateTime } from 'luxon';
import { catchError, from, of, switchMap } from 'rxjs';
import { PartnerFilter, searchPartnerAggregation } from '../api/subscribePartnerByFilter.js';
import { Partner } from './Partner.js';

/**
 * @entity Fundingpot
 */
@Entity()
export class Fundingpot extends MongoEntity {
  public static override _class = 'Fundingpot';
  public static override _collectionName = 'foerdertoepfe';

  @Field(() => ID)
  public partnerID!: string;

  @DataLoadedField(() => Partner, fundingPot => fundingPot.partnerID)
  public partner: Maybe<Partner>;

  @Field(() => String)
  public name!: string;

  @Field(() => DateTime)
  public start!: DateTime;

  @Field(() => DateTime)
  public end!: DateTime;

  @Subscription(() => [Fundingpot])
  static subscribeFundingpotForPartner(@Arg('partnerID', () => String) partnerID: string) {
    return (gqlFields: BaseQLSelectionSet<Fundingpot>) => {
      const selector = { _class: Fundingpot._class, partnerID };
      const handle = getCollectionHandle<Fundingpot>(Fundingpot._collectionName);
      return from(publishFilter(Fundingpot._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector);
        }),
        catchError(e => {
          return of([] as Fundingpot[]);
        }),
      );
    };
  }

  @Mutation(() => Int)
  public static updateFoerdertoepfeForPartner(
    @Arg('partnerID', () => String) partnerID: string,
    @Arg('foerdertoepfe', () => [Fundingpot]) foerdertoepfe: Fundingpot[],
  ) {
    return async (gqlFields: void) => {
      const c = await getCollection(Fundingpot);
      while (true) {
        const result = await c.findOneAndDelete({ partnerID, _id: { $nin: foerdertoepfe.map(ft => ft._id) } });
        if (!result) break;
      }

      for (const ft of foerdertoepfe) {
        try {
          await ft.create();
        } catch (e) {
          await ft.save();
        }
      }
    };
  }

  @Subscription(() => [Fundingpot])
  static subscribeFundingpotsPaginatedFiltered(
    @Arg('skip', () => Int) skip: number,
    @Arg('limit', () => Int) limit: number,
    @Arg('filter', () => PartnerFilter) filter: PartnerFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<Fundingpot>) => {
      return subscribePaginatedAggregation(Fundingpot, skip, limit, {}, [
        ...searchPartnerAggregation(filter),
        { $sort: filter.getMongoOptions().sort },
      ]);
    };
  }

  override toString() {
    return this.name;
  }
}
